const MAX_UPLOAD_BYTES = 6 * 1024 * 1024;
const ALLOWED_UPLOAD_EXTENSIONS = ['png', 'svg', 'pdf', 'ai', 'eps'];
const UPLOAD_LIMIT_MESSAGE = 'This file is over 6 MB. Choose a file that is 6 MB or smaller.';
const UPLOAD_TYPE_MESSAGE = 'Use a PNG, SVG, PDF, AI, or EPS file.';

function uploadExtension(file) {
  const name = (file && file.name ? file.name : '').toLowerCase();
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop() : '';
}

function rejectUploadFile(input, label, error, message) {
  input.value = '';
  if (label) label.textContent = 'No file chosen';
  if (error) {
    error.hidden = false;
    error.textContent = message;
  }
}

function applyUploadFile(input, label) {
  const wrap = input.closest('.custom-logo-dropdown__upload, .custom-design-dropdown__upload');
  const error = wrap && wrap.querySelector('[data-upload-size-error]');
  const file = input.files && input.files[0];

  if (file && ALLOWED_UPLOAD_EXTENSIONS.indexOf(uploadExtension(file)) === -1) {
    rejectUploadFile(input, label, error, UPLOAD_TYPE_MESSAGE);
    return;
  }

  if (file && file.size > MAX_UPLOAD_BYTES) {
    rejectUploadFile(input, label, error, UPLOAD_LIMIT_MESSAGE);
    return;
  }

  if (error) {
    error.hidden = true;
    error.textContent = '';
  }
  if (label) label.textContent = file ? file.name : 'No file chosen';
}

if (!customElements.get('logo-sequence-modal')) {
  customElements.define(
    'logo-sequence-modal',
    class LogoSequenceModal extends ModalDialog {
      constructor() {
        super();
        this.slider = this.querySelector('[data-logo-sequence-slider]');
        this.prevButton = this.querySelector('[data-logo-sequence-prev]');
        this.nextButton = this.querySelector('[data-logo-sequence-next]');
        this.index = 0;

        if (this.prevButton) {
          this.prevButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.goTo(this.index - 1);
          });
        }
        if (this.nextButton) {
          this.nextButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.goTo(this.index + 1);
          });
        }
        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
          if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        });
        this.goTo(0);
      }

      get slides() {
        if (!this.slider) return [];
        return Array.from(this.slider.querySelectorAll('[data-logo-sequence-slide]'));
      }

      show(opener) {
        super.show(opener);
        this.goTo(0);
      }

      goTo(index) {
        const slides = this.slides;
        if (!slides.length) return;

        this.index = Math.min(Math.max(index, 0), slides.length - 1);
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === this.index;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
        });

        const hideNav = slides.length <= 1;
        if (this.prevButton) {
          this.prevButton.disabled = this.index <= 0;
          this.prevButton.toggleAttribute('hidden', hideNav);
        }
        if (this.nextButton) {
          this.nextButton.disabled = this.index >= slides.length - 1;
          this.nextButton.toggleAttribute('hidden', hideNav);
        }
      }
    }
  );
}

if (!customElements.get('custom-logo-dropdown')) {
  customElements.define(
    'custom-logo-dropdown',
    class CustomLogoDropdown extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.select = this.querySelector('[data-logo-mode]');
        this.panel = this.querySelector('[data-logo-custom-panel]');
        this.fields = this.querySelectorAll('[data-logo-custom-field]');
        this.customValue = this.dataset.customValue || '';
        this.onDocumentClick = this.onDocumentClick.bind(this);

        this.addEventListener('click', (event) => this.onStopToggle(event), true);

        if (this.select) {
          this.select.addEventListener('change', () => this.sync());
          this.sync();
        }

        this.querySelectorAll('[data-logo-file]').forEach((input) => {
          input.addEventListener('change', () => this.updateFileLabel(input));
        });

        this.querySelectorAll('[data-logo-designer-toggle]').forEach((toggle) => {
          toggle.addEventListener('change', () => this.sync());
        });

        this.bindSequenceMenus();
        document.addEventListener('click', this.onDocumentClick);
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() !== 'ESCAPE') return;
          if (!this.querySelector('[data-logo-sequence].is-open')) return;
          event.stopPropagation();
          this.closeSequenceMenus();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      onStopToggle(event) {
        if (!event.target.closest('[data-logo-stop-toggle]')) return;
        event.preventDefault();
      }

      bindSequenceMenus() {
        this.querySelectorAll('[data-logo-sequence]').forEach((root) => {
          const toggle = root.querySelector('[data-logo-sequence-toggle]');
          const menu = root.querySelector('[data-logo-sequence-menu]');
          const options = root.querySelectorAll('[data-logo-sequence-option]');

          if (!toggle || !menu) return;

          toggle.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const open = !root.classList.contains('is-open');
            this.closeSequenceMenus(root);
            this.setSequenceOpen(root, open);
          });

          options.forEach((option) => {
            option.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              this.applySequenceValue(root, option);
              this.setSequenceOpen(root, false);
            });
          });

          const selected =
            root.querySelector('[data-logo-sequence-option][aria-selected="true"]') || options[0];
          if (selected) this.applySequenceValue(root, selected);
        });
      }

      applySequenceValue(root, option) {
        if (!root || !option) return;
        const toggle = root.querySelector('[data-logo-sequence-toggle]');
        const input = root.querySelector('[data-logo-sequence-input]');
        const chosen = root.querySelector('[data-logo-sequence-chosen]');
        const options = root.querySelectorAll('[data-logo-sequence-option]');
        const value = (option.getAttribute('data-value') || option.textContent || '').trim();

        options.forEach((item) => {
          item.setAttribute('aria-selected', item === option ? 'true' : 'false');
          item.classList.toggle('is-selected', item === option);
        });
        if (input) input.value = value;
        if (chosen) {
          chosen.textContent = value;
          chosen.hidden = !value;
        }
        if (toggle) {
          toggle.setAttribute('aria-label', value ? `Logo order: ${value}` : 'Logo order');
        }
      }

      setSequenceOpen(root, open) {
        const toggle = root.querySelector('[data-logo-sequence-toggle]');
        const menu = root.querySelector('[data-logo-sequence-menu]');
        root.classList.toggle('is-open', open);
        if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (menu) menu.hidden = !open;
      }

      closeSequenceMenus(exceptRoot) {
        this.querySelectorAll('[data-logo-sequence].is-open').forEach((root) => {
          if (root === exceptRoot) return;
          this.setSequenceOpen(root, false);
        });
      }

      onDocumentClick(event) {
        if (event.target.closest('[data-logo-sequence]')) return;
        this.closeSequenceMenus();
      }

      isCustom() {
        return this.select && this.select.value === this.customValue;
      }

      sync() {
        const custom = this.isCustom();
        if (this.panel) this.panel.hidden = !custom;
        this.fields.forEach((field) => {
          field.disabled = !custom;
          if (!custom && field.type === 'file') {
            field.value = '';
            this.updateFileLabel(field);
          }
          if (!custom && field.tagName === 'TEXTAREA') {
            field.value = '';
          }
        });
        this.syncDesignerChoices(custom);
      }

      syncDesignerChoices(custom) {
        this.querySelectorAll('[data-logo-designer]').forEach((root) => {
          const toggle = root.querySelector('[data-logo-designer-toggle]');
          const valueInput = root.querySelector('[data-logo-designer-value]');
          const customerFields = root.parentElement
            ? root.parentElement.querySelector('[data-logo-customer-fields]')
            : null;
          const chosen = Boolean(toggle && toggle.checked);

          if (customerFields) {
            customerFields.hidden = chosen;
            customerFields.querySelectorAll('[data-logo-custom-field]').forEach((field) => {
              field.disabled = !custom || chosen;
            });
          }

          if (valueInput) {
            valueInput.disabled = !custom || !chosen;
          }
        });
      }

      updateFileLabel(input) {
        const label = this.querySelector(`[data-logo-file-name="${input.id}"]`);
        applyUploadFile(input, label);
      }
    }
  );
}

if (!customElements.get('custom-design-dropdown')) {
  customElements.define(
    'custom-design-dropdown',
    class CustomDesignDropdown extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.select = this.querySelector('[data-design-mode]');
        this.noneValue = this.dataset.noneValue || '';

        if (this.select) {
          this.select.addEventListener('change', () => this.sync());
          this.sync();
        }

        this.querySelectorAll('[data-design-file]').forEach((input) => {
          input.addEventListener('change', () => this.updateFileLabel(input));
        });
      }

      selectedIndex() {
        if (!this.select || !this.select.selectedOptions.length) return '';
        return this.select.selectedOptions[0].getAttribute('data-design-index') || '';
      }

      isCustom() {
        if (!this.select) return false;
        const index = this.selectedIndex();
        return Boolean(index) && this.select.value !== this.noneValue;
      }

      sync() {
        const custom = this.isCustom();
        const index = this.selectedIndex();

        this.querySelectorAll('[data-design-panel]').forEach((panel) => {
          const match = custom && panel.getAttribute('data-design-index') === index;
          panel.hidden = !match;
          panel.querySelectorAll('[data-design-field]').forEach((field) => {
            field.disabled = !match;
            if (!match && field.type === 'file') {
              field.value = '';
              this.updateFileLabel(field);
            }
            if (!match && field.tagName === 'TEXTAREA') {
              field.value = '';
            }
          });
        });
      }

      updateFileLabel(input) {
        const label = this.querySelector(`[data-design-file-name="${input.id}"]`);
        applyUploadFile(input, label);
      }
    }
  );
}
